/**
 * Who is offered Yara, and which half of them is the control group.
 *
 * owner 2026-09-27: Yara is offered only where it is actually better than plain
 * search — a patient describing a symptom. Measured the week before: 7,591
 * devices searched a symptom and only 34% clicked any result, against 70% for
 * every other query (ghane ops-log 2026-09-27). A named doctor, a specialty or a
 * hospital is plain search's job; the doctor's page has the booking button.
 *
 * Pure functions on purpose: both the eligibility rule and the split are the
 * experiment's definition, so they must be testable without a browser.
 */

function normalize(raw: unknown): string {
  return String(raw ?? '')
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[‌‏‎ـ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Pain words, including joined compounds (سردرد، کمردرد) — but not a surname that starts with درد. */
const PAIN = /درد(\s|$|ها)/;

/** Whole-word symptoms. Normalized spelling: no half-space, Persian ی/ک. */
const SYMPTOM_WORDS = [
  'سوزش', 'سرفه', 'خارش', 'ورم', 'تورم', 'تهوع', 'سرگیجه', 'اسهال', 'یبوست', 'خونریزی',
  'ریزش مو', 'بیخوابی', 'بی خوابی', 'اضطراب', 'افسردگی', 'استرس', 'تنگی نفس', 'نفس تنگی',
  'تب', 'لرز', 'بیحسی', 'بی حسی', 'گزگز', 'کرختی', 'تپش قلب', 'نفخ', 'دلپیچه', 'دل پیچه',
  'آبریزش', 'عطسه', 'کبودی', 'کهیر', 'جوش', 'آکنه', 'تاری دید', 'وزوز', 'خروپف', 'بی اشتهایی',
  'رفلاکس', 'ترش کردن', 'حالت تهوع', 'سرماخوردگی', 'آنفولانزا',
];
const WORDS = new RegExp(`(^|\\s)(${SYMPTOM_WORDS.join('|')})(\\s|$)`);

/**
 * «متخصص درد»، «کلینیک ریزش مو»، «مرکز درمان اضطراب» are a specialty or a place,
 * not a complaint — for any symptom word, not only pain.
 */
const PLACE_OR_SPECIALTY = /(متخصص|فلوشیپ|فوق تخصص|کلینیک|درمانگاه|مرکز|طب|بیهوشی و)\s+(درمان\s+)?/;
const SYMPTOM_AS_SPECIALTY = new RegExp(
  `${PLACE_OR_SPECIALTY.source}(\\S*درد|${SYMPTOM_WORDS.join('|')})(\\s|$)`
);

export function isSymptomQuery(raw: unknown): boolean {
  const q = normalize(raw);
  if (!q) return false;
  if (SYMPTOM_AS_SPECIALTY.test(q)) return false;
  return PAIN.test(q) || WORDS.test(q);
}

export type SymptomInviteArm = 'invite' | 'control';

/**
 * Salted so this split is independent of every other terminal-hashed experiment
 * (GrowthBook buckets on the same `terminal_id`).
 */
const SALT = 'yara-symptom-invite-2026-09-27';

/** FNV-1a then a murmur3 finalizer — FNV alone has a weak low bit. */
function hash32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * The device's arm, or null when there is no `terminal_id` — a device we cannot
 * follow to a booking cannot be in the experiment at all.
 */
export function symptomInviteArm(terminalId: unknown): SymptomInviteArm | null {
  const id = String(terminalId ?? '').trim();
  if (!id) return null;
  return hash32(`${SALT}:${id}`) % 2 === 0 ? 'invite' : 'control';
}

/* ---- session memory shared by the results list and the offer ---- */

export const CLICKED_EVENT = 'yara-symptom-result-click';
export const clickedKey = (text: string) => `yara_si_clicked:${text.trim()}`;
export const eligibleKey = (text: string) => `yara_si_eligible:${text.trim()}`;
export const dismissedKey = (text: string) => `yara_si_dismissed:${text.trim()}`;

export function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** A click on something actionable in the results, not in the filter column. */
export function isResultClick(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (!el?.closest) return false;
  return Boolean(el.closest('a,button,[role="button"]')) && !el.closest('[data-yara-ignore]');
}

/**
 * Called by the results list on any click inside it. Remembered for the session,
 * so coming back from a doctor's profile to the same search does not show the
 * offer either.
 */
export function markSearchResultClick(text: string): void {
  if (!String(text ?? '').trim()) return;
  try {
    session()?.setItem(clickedKey(text), '1');
  } catch {
    /* storage full / blocked — the in-page event below still hides the bar */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CLICKED_EVENT, { detail: text.trim() }));
}
